/**
 * Binance Futures Book Ticker WebSocket Client
 *
 * Native WebSocket implementation for Binance Futures !bookTicker stream.
 * This bypasses CCXT which doesn't support watchBidsAsks for Binance Futures.
 *
 * Stream: wss://fstream.binance.com/ws/!bookTicker
 *
 * Response format:
 * {
 *   "e": "bookTicker",     // event type
 *   "u": 400900217,        // order book updateId
 *   "E": 1568014460893,    // event time
 *   "T": 1568014460891,    // transaction time
 *   "s": "BNBUSDT",        // symbol
 *   "b": "25.35190000",    // best bid price
 *   "B": "31.21000000",    // best bid qty
 *   "a": "25.36520000",    // best ask price
 *   "A": "40.66000000"     // best ask qty
 * }
 */

export interface BookTickerData {
  /** Unified symbol format (e.g., "BTC/USDT") */
  symbol: string;

  /** Best bid price */
  bestBid: number;

  /** Best bid quantity */
  bestBidQty: number;

  /** Best ask price */
  bestAsk: number;

  /** Best ask quantity */
  bestAskQty: number;

  /** Order book update ID */
  updateId: number;

  /** Event timestamp (milliseconds) */
  timestamp: number;
}

/** Raw WebSocket message from Binance */
interface RawBookTickerMessage {
  e: string;      // "bookTicker"
  u: number;      // order book updateId
  E: number;      // event time
  T: number;      // transaction time
  s: string;      // symbol (e.g., "BNBUSDT")
  b: string;      // best bid price
  B: string;      // best bid qty
  a: string;      // best ask price
  A: string;      // best ask qty
}

/** Connection state */
type ConnectionState = 'disconnected' | 'connecting' | 'connected' | 'reconnecting';

/** Callback types */
export type BookTickerCallback = (data: BookTickerData) => void;
export type ConnectionCallback = (state: ConnectionState) => void;
export type ErrorCallback = (error: Error) => void;

/**
 * Binance Futures Book Ticker WebSocket Client
 *
 * Connects to the all-symbols book ticker stream for real-time best bid/ask prices.
 */
export class BinanceFuturesBookTickerClient {
  /** WebSocket connection */
  private ws: WebSocket | null = null;

  /** Connection state */
  private state: ConnectionState = 'disconnected';

  /** Book ticker data cache */
  private bookTickerCache: Map<string, BookTickerData> = new Map();

  /** Callback for book ticker updates */
  private onBookTickerUpdate: BookTickerCallback | null = null;

  /** Callback for connection state changes */
  private onConnectionStateChange: ConnectionCallback | null = null;

  /** Callback for errors */
  private onError: ErrorCallback | null = null;

  /** Reconnection state */
  private reconnectAttempts: number = 0;
  private reconnectTimeout: ReturnType<typeof setTimeout> | null = null;

  /** WebSocket URL for all-symbol book ticker stream */
  private readonly WS_URL = 'wss://fstream.binance.com/ws/!bookTicker';

  /** Reconnection configuration */
  private readonly INITIAL_RETRY_DELAY_MS = 1000;
  private readonly MAX_RETRY_DELAY_MS = 60000;
  private readonly BACKOFF_MULTIPLIER = 2;

  /** Ping interval to keep connection alive */
  private pingInterval: ReturnType<typeof setInterval> | null = null;
  private readonly PING_INTERVAL_MS = 30000; // 30 seconds

  /**
   * Set callback for book ticker updates
   */
  setOnBookTickerUpdate(callback: BookTickerCallback): void {
    this.onBookTickerUpdate = callback;
  }

  /**
   * Set callback for connection state changes
   */
  setOnConnectionStateChange(callback: ConnectionCallback): void {
    this.onConnectionStateChange = callback;
  }

  /**
   * Set callback for errors
   */
  setOnError(callback: ErrorCallback): void {
    this.onError = callback;
  }

  /**
   * Connect to the book ticker stream
   */
  connect(): void {
    if (this.state === 'connected' || this.state === 'connecting') {
      console.log('[BookTicker] Already connected or connecting');
      return;
    }

    this.setState('connecting');
    console.log('[BookTicker] Connecting to', this.WS_URL);

    try {
      this.ws = new WebSocket(this.WS_URL);

      // CRITICAL: Only use onmessage, NOT addEventListener
      // Using both causes messages to stop after 15-30 seconds
      this.ws.onopen = () => {
        console.log('[BookTicker] Connected successfully');
        this.setState('connected');
        this.reconnectAttempts = 0;
        this.startPingInterval();
      };

      this.ws.onmessage = (event) => {
        this.handleMessage(event.data);
      };

      this.ws.onclose = (event) => {
        console.warn('[BookTicker] Connection closed:', event.code, event.reason);
        this.cleanup();
        this.scheduleReconnect();
      };

      this.ws.onerror = (event) => {
        console.error('[BookTicker] WebSocket error:', event);
        this.onError?.(new Error('WebSocket error'));
      };
    } catch (error) {
      console.error('[BookTicker] Failed to create WebSocket:', error);
      this.onError?.(error instanceof Error ? error : new Error(String(error)));
      this.scheduleReconnect();
    }
  }

  /**
   * Disconnect from the book ticker stream
   */
  disconnect(): void {
    console.log('[BookTicker] Disconnecting...');
    this.cleanup();
    this.setState('disconnected');
    this.bookTickerCache.clear();

    // Clear any pending reconnect
    if (this.reconnectTimeout) {
      clearTimeout(this.reconnectTimeout);
      this.reconnectTimeout = null;
    }
  }

  /**
   * Get cached book ticker data for a symbol
   * @param symbol - Unified symbol format (e.g., "BTC/USDT")
   */
  getBookTicker(symbol: string): BookTickerData | undefined {
    return this.bookTickerCache.get(symbol);
  }

  /**
   * Get all cached book ticker data
   */
  getAllBookTickers(): BookTickerData[] {
    return Array.from(this.bookTickerCache.values());
  }

  /**
   * Get current connection state
   */
  getState(): ConnectionState {
    return this.state;
  }

  /**
   * Check if connected
   */
  isConnected(): boolean {
    return this.state === 'connected';
  }

  /**
   * Handle incoming WebSocket message
   */
  private handleMessage(data: string): void {
    try {
      const raw: RawBookTickerMessage = JSON.parse(data);

      // Validate message type
      if (raw.e !== 'bookTicker') {
        return;
      }

      // Skip non-USDT pairs (delivery contracts, USDC, etc.)
      if (!raw.s.endsWith('USDT')) {
        return;
      }

      // Skip delivery contracts (e.g., BTCUSDT_251226)
      if (raw.s.includes('_')) {
        return;
      }

      // Convert symbol from BTCUSDT to BTC/USDT
      const baseAsset = raw.s.slice(0, -4); // Remove "USDT"
      const normalizedSymbol = `${baseAsset}/USDT`;

      // Create book ticker data
      const bookTicker: BookTickerData = {
        symbol: normalizedSymbol,
        bestBid: parseFloat(raw.b),
        bestBidQty: parseFloat(raw.B),
        bestAsk: parseFloat(raw.a),
        bestAskQty: parseFloat(raw.A),
        updateId: raw.u,
        timestamp: raw.E,
      };

      // Update cache
      this.bookTickerCache.set(normalizedSymbol, bookTicker);

      // Notify callback
      this.onBookTickerUpdate?.(bookTicker);
    } catch (error) {
      console.error('[BookTicker] Failed to parse message:', error);
    }
  }

  /**
   * Update connection state and notify callback
   */
  private setState(newState: ConnectionState): void {
    if (this.state !== newState) {
      this.state = newState;
      this.onConnectionStateChange?.(newState);
    }
  }

  /**
   * Start ping interval to keep connection alive
   */
  private startPingInterval(): void {
    this.stopPingInterval();

    this.pingInterval = setInterval(() => {
      if (this.ws?.readyState === WebSocket.OPEN) {
        // Send a ping frame (Binance expects pong response)
        // Note: Browser WebSocket doesn't expose ping/pong frames directly
        // Binance handles this automatically, but we can send a ping message
        // that Binance will ignore (it won't break the connection)
      }
    }, this.PING_INTERVAL_MS);
  }

  /**
   * Stop ping interval
   */
  private stopPingInterval(): void {
    if (this.pingInterval) {
      clearInterval(this.pingInterval);
      this.pingInterval = null;
    }
  }

  /**
   * Clean up WebSocket connection
   */
  private cleanup(): void {
    this.stopPingInterval();

    if (this.ws) {
      // Remove all event handlers to prevent memory leaks
      this.ws.onopen = null;
      this.ws.onmessage = null;
      this.ws.onclose = null;
      this.ws.onerror = null;

      // Close if still open
      if (this.ws.readyState === WebSocket.OPEN || this.ws.readyState === WebSocket.CONNECTING) {
        this.ws.close();
      }

      this.ws = null;
    }
  }

  /**
   * Schedule reconnection with exponential backoff
   */
  private scheduleReconnect(): void {
    if (this.state === 'disconnected') {
      // User explicitly disconnected, don't reconnect
      return;
    }

    this.setState('reconnecting');
    this.reconnectAttempts++;

    // Calculate delay with exponential backoff
    const delay = Math.min(
      this.INITIAL_RETRY_DELAY_MS * Math.pow(this.BACKOFF_MULTIPLIER, this.reconnectAttempts - 1),
      this.MAX_RETRY_DELAY_MS
    );

    console.log(
      `[BookTicker] Scheduling reconnect in ${delay}ms (attempt ${this.reconnectAttempts})`
    );

    this.reconnectTimeout = setTimeout(() => {
      this.reconnectTimeout = null;
      this.connect();
    }, delay);
  }
}

// Singleton instance for global access
let bookTickerClient: BinanceFuturesBookTickerClient | null = null;

/**
 * Get the global book ticker client instance
 */
export function getBookTickerClient(): BinanceFuturesBookTickerClient {
  if (!bookTickerClient) {
    bookTickerClient = new BinanceFuturesBookTickerClient();
  }
  return bookTickerClient;
}
